import { AlertTriangle, CloudOff, Loader2, type LucideIcon, RotateCw, Sprout } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNetworkError } from '@/lib/offlineQueue';
import { cn } from '@farmos/ui/utils';
import { Button } from '@farmos/ui/button';

export function Spinner({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div role="status" className={cn('flex items-center justify-center p-8 text-muted', className)}>
      <Loader2 className="size-6 animate-spin" aria-hidden />
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );
}

/**
 * Nothing here yet. Says why it's empty and, when there's an obvious next step, offers it.
 * A plain `children` still works for a one-line message.
 */
export function Empty({
  children,
  title,
  icon: Icon = Sprout,
  actions,
}: {
  children?: ReactNode;
  title?: ReactNode;
  icon?: LucideIcon;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[24px] border border-dashed border-line bg-panel/60 px-6 py-12 text-center text-sm text-muted">
      <span className="grid size-14 place-items-center rounded-[18px] bg-brand-soft text-brand">
        <Icon className="size-7" aria-hidden />
      </span>
      {title && <h2 className="text-base font-semibold text-ink">{title}</h2>}
      {children && <div className="max-w-sm">{children}</div>}
      {actions && <div className="mt-1 flex flex-wrap justify-center gap-2">{actions}</div>}
    </div>
  );
}

/** Placeholder blocks shaped like the content that is loading. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton', className)} aria-hidden />;
}

export function PageSkeleton() {
  const { t } = useTranslation();
  return (
    <div role="status" className="space-y-4">
      <span className="sr-only">{t('common.loading')}</span>
      <Skeleton className="h-9 w-48" />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-28 rounded-[20px]" />
        ))}
      </div>
      <Skeleton className="h-72 rounded-[24px]" />
    </div>
  );
}

/**
 * Something failed to load or save. Says what happened in plain words, offers "try again" when
 * the caller can retry, and keeps the raw message behind "technical details".
 */
export function ErrorBox({
  error,
  onRetry,
  title,
}: {
  error: unknown;
  onRetry?: () => void;
  title?: ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const msg = error instanceof Error ? error.message : String(error);
  const offline = isNetworkError(error);
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-3 rounded-[24px] border border-gap/20 bg-gap/[0.04] px-6 py-10 text-center"
    >
      <span className="grid size-14 place-items-center rounded-[18px] bg-gap/10 text-gap">
        {offline ? (
          <CloudOff className="size-7" aria-hidden />
        ) : (
          <AlertTriangle className="size-7" aria-hidden />
        )}
      </span>
      <h2 className="text-base font-semibold">
        {title ?? t(offline ? 'errors.offline_title' : 'errors.load_title')}
      </h2>
      <p className="max-w-sm text-sm text-muted">
        {t(offline ? 'errors.offline_body' : 'errors.load_body')}
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        {onRetry && (
          <Button onClick={onRetry}>
            <RotateCw /> {t('errors.retry')}
          </Button>
        )}
        <Button variant="ghost" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {t('errors.details')}
        </Button>
      </div>
      {open && (
        <pre
          dir="ltr"
          className="max-w-full overflow-auto whitespace-pre-wrap rounded-xl bg-tile p-3 text-start text-xs text-muted"
        >
          {msg}
        </pre>
      )}
    </div>
  );
}

export function Badge({
  children,
  tone = 'muted',
}: {
  children: ReactNode;
  tone?: 'muted' | 'ng' | 'gap' | 'money' | 'sold';
}) {
  const cls = {
    muted: 'bg-soft text-muted',
    ng: 'bg-ng/12 text-ng',
    gap: 'bg-gap/10 text-gap',
    money: 'bg-money/12 text-money',
    sold: 'bg-sold/12 text-sold',
  }[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium',
        cls,
      )}
    >
      {children}
    </span>
  );
}
